import type { HttpAdapter } from "../core/adapters/axios.adapter"

/** GET /search/token — a one-hour Meilisearch tenant token (tbw-backend). */
export interface SearchToken {
  token: string
  host: string
  expiresAt: string
}

/** Renew this long before expiry, so a query never goes out with a dead token. */
const RENEW_BEFORE_MS = 60_000

/**
 * The search box used to ship a Meilisearch key in the public bundle, which let
 * anyone list every person and company name without an account (audit 15/10).
 * It now uses a short-lived tenant token that only a signed-in user can get.
 */
export class SearchTokenService {
  private current?: SearchToken
  private pending?: Promise<SearchToken | null>

  constructor(private httpAdapter: HttpAdapter, private now: () => number = Date.now) {}

  /** A token valid for at least another minute, or null when the API refuses (signed out, not configured). */
  async get(): Promise<SearchToken | null> {
    if (this.current && Date.parse(this.current.expiresAt) - this.now() > RENEW_BEFORE_MS) return this.current
    // One request at a time: the box searches on every keystroke.
    this.pending ??= this.fetch().finally(() => { this.pending = undefined })
    return this.pending
  }

  private async fetch(): Promise<SearchToken | null> {
    try {
      const response = await this.httpAdapter.get<SearchToken>("search/token")
      this.current = response.data
      return this.current
    } catch (error) {
      console.error("[search] token request failed", error)
      return null
    }
  }
}
